
class AccountingJournalTemplateEntryCell extends UITableViewCell
{
    // Key options per domain and matchMode. Each option is [strategyKey, label, formula].
    // Formulas are full expressions: every {{ ... }} placeholder is replaced
    // with its resolved value before evaluation. Operators ( || , * , etc. )
    // live outside the braces.
    // For "customLedgerAccount" formula is null and the stored keyFormula is the
    // selected ledger account title (plain text, no braces).
    // "baseCustomAccount" works like "customLedgerAccount" but the selected account acts
    // as a base: the generateAccountFromBase switch tells the server to generate the
    // customer account number incrementally from the base account prefix.
    private static accountOptions: Array<[string, string, string|null]> = [
        ["customLedgerAccount", "CUSTOM_LEDGER_ACCOUNT", null],
        ["baseCustomAccount", "BASE_CUSTOM_ACCOUNT", null]
    ];

    // Purchase invoice domain.
    // matchMode 0 (By Document): Supplier Ledger Account
    // matchMode 1 (By Line):     Line Ledger Account
    private static purchaseOptionsByMatchMode: { [key: number]: Array<[string, string, string|null]> } = {
        0: [["supplierLedgerAccount", "SUPPLIER_LEDGER_ACCOUNT", "{{legalEntity.purchaseLedgerAccount}}"]],
        1: [["lineLedgerAccount", "LINE_LEDGER_ACCOUNT", "{{product.purchaseLedgerAccount}} || {{conceptItem.purchaseLedgerAccount}}"],
            // The line classification (e.g. fixed assets) carries its own ledger account
            ["classificationLedgerAccount", "CLASSIFICATION_LEDGER_ACCOUNT", "{{lineClassification.ledgerAccount}} || {{product.purchaseLedgerAccount}}"],
            // The accounts set in the ledger account reconciliation: the line's own, else its
            // invoice's default, else the classification's or the product's
            ["lineOrDocumentLedgerAccount", "LINE_OR_DOCUMENT_LEDGER_ACCOUNT", "{{ledgerAccount}} || {{document.ledgerAccount}} || {{lineClassification.ledgerAccount}} || {{product.purchaseLedgerAccount}} || {{conceptItem.purchaseLedgerAccount}}"]]
    };

    // Sales-side domains (sales invoice, POS receipt, archived invoice): same shape, sales accounts.
    private static salesOptionsByMatchMode: { [key: number]: Array<[string, string, string|null]> } = {
        0: [["customerLedgerAccount", "CUSTOMER_LEDGER_ACCOUNT", "{{legalEntity.salesLedgerAccount}}"]],
        1: [["lineLedgerAccount", "LINE_LEDGER_ACCOUNT", "{{product.salesLedgerAccount}} || {{conceptItem.salesLedgerAccount}}"],
            ["classificationLedgerAccount", "CLASSIFICATION_LEDGER_ACCOUNT", "{{lineClassification.ledgerAccount}} || {{product.salesLedgerAccount}}"],
            ["lineOrDocumentLedgerAccount", "LINE_OR_DOCUMENT_LEDGER_ACCOUNT", "{{ledgerAccount}} || {{document.ledgerAccount}} || {{lineClassification.ledgerAccount}} || {{product.salesLedgerAccount}} || {{conceptItem.salesLedgerAccount}}"]]
    };

    // Bank movement domain. The movement itself (By Document) books against the
    // bank account or the analytic account's default ledger account; a By Link
    // group iterates the movement's document links, whose legal entity is the
    // customer/supplier the money was collected from or paid to.
    private static bankOptionsByMatchMode: { [key: number]: Array<[string, string, string|null]> } = {
        0: [["bankAccountLedgerAccount", "BANK_ACCOUNT_LEDGER_ACCOUNT", "{{bankAccount.ledgerAccount}}"],
            ["analyticLedgerAccount", "ANALYTIC_LEDGER_ACCOUNT", "{{analyticAccount.ledgerAccount}}"]],
        2: [["linkCustomerLedgerAccount", "LINK_CUSTOMER_LEDGER_ACCOUNT", "{{legalEntity.salesLedgerAccount}}"],
            ["linkSupplierLedgerAccount", "LINK_SUPPLIER_LEDGER_ACCOUNT", "{{legalEntity.purchaseLedgerAccount}}"],
            ["bankAccountLedgerAccount", "BANK_ACCOUNT_LEDGER_ACCOUNT", "{{document.bankAccount.ledgerAccount}}"],
            ["analyticLedgerAccount", "ANALYTIC_LEDGER_ACCOUNT", "{{document.analyticAccount.ledgerAccount}}"]]
    };

    // Analytic account templates run against any tagged source (bank movements
    // and invoices alike), so they offer both catalogs plus the analytic default
    // for invoice lines.
    private static analyticExtraOptionsByMatchMode: { [key: number]: Array<[string, string, string|null]> } = {
        1: [["analyticLedgerAccount", "ANALYTIC_LEDGER_ACCOUNT", "{{document.analyticAccount.ledgerAccount}}"]]
    };

    // Event type values match AccountingEventType in AccountingJournalEnums.swift
    // (5 = bank movement); 100 is the analytic account pseudo-domain of
    // AccountingJournalTemplateViewController.
    private static keyOptions(eventType:number, matchMode:number): Array<[string, string, string|null]> {
        let bank = AccountingJournalTemplateEntryCell.bankOptionsByMatchMode[matchMode] ?? [];
        let purchase = AccountingJournalTemplateEntryCell.purchaseOptionsByMatchMode[matchMode] ?? [];
        let sales = AccountingJournalTemplateEntryCell.salesOptionsByMatchMode[matchMode] ?? [];
        let options:Array<[string, string, string|null]> = [];
        if (eventType == 5) options = bank;
        else if (eventType == 2) options = purchase;
        else if (eventType == AccountingJournalTemplateViewController.analyticAccountDomain) {
            options = bank.concat(sales, purchase, AccountingJournalTemplateEntryCell.analyticExtraOptionsByMatchMode[matchMode] ?? []);
        }
        else options = sales; // 1 sales invoice, 3 POS receipt, 4 archived invoice
        return options.concat(AccountingJournalTemplateEntryCell.accountOptions);
    }

    private static isAccountKey(key:string|null):boolean {
        return key == "customLedgerAccount" || key == "baseCustomAccount";
    }

    private keyFormatComboBox:MUIComboBox = null;
    private accountDropdown:MUIButton = null;
    private generateAccountSwitch:MUISwitchButton = null;
    private valueFormulaTextField:MUITextField = null;
    private sideSegmented:MUISegmentedControl = null;

    private _keyOptions:Array<[string, string, string|null]> = [];
    private _activeKey:string|null = null;
    private _eventType:number = -1;

    // Domain the template belongs to; set before `item` so the key catalog matches.
    set eventType(type:number) {
        this._eventType = type;
    }

    awakeFromHTML()
    {
        this.keyFormatComboBox = MUIOutlet(this, "key-format-cb", "MUIComboBox");
        if (this.keyFormatComboBox) this.keyFormatComboBox.setOnChangeAction(this, function(this:AccountingJournalTemplateEntryCell, control:MUIComboBox, value:string){
            let index = parseInt(value);
            let option = (index >= 0 && this._keyOptions[index] != null) ? this._keyOptions[index] : null;
            let key = option != null ? option[0] : null;
            this._activeKey = key;
            if (this._item != null) {
                if (option == null) {
                    this._item.keyFormula = null;
                } else if (AccountingJournalTemplateEntryCell.isAccountKey(key)) {
                    this._item.keyFormula = this._item.ledgerAccount?.name ?? null;
                } else {
                    this._item.keyFormula = option[2];
                }
                // Clear ledger account when not fixed
                if (AccountingJournalTemplateEntryCell.isAccountKey(key) == false) {
                    this._item.ledgerAccount = null;
                    if (this.accountDropdown) this.accountDropdown.title = MIOLocalizeString("SELECT_LEDGER_ACCOUNT", "SELECT LEDGER ACCOUNT");
                }
                // The incremental generation only applies to the base custom account strategy
                if (key != "baseCustomAccount") {
                    this._item.generateAccountFromBase = false;
                    if (this.generateAccountSwitch) this.generateAccountSwitch.setOn(false);
                }
            }
            this.updateAccountDropdownEnabled(key);
        });

        this.accountDropdown = MUIOutlet(this, "account-dd", "MUIButton");
        if (this.accountDropdown) this.accountDropdown.setAction(this, function(this:AccountingJournalTemplateEntryCell){
            if (AccountingJournalTemplateEntryCell.isAccountKey(this._activeKey) == false) return;
            this.showSelectLedgerAccount(null, false);
        });

        this.generateAccountSwitch = MUIOutlet(this, "generate-account-sb", "MUISwitchButton");
        if (this.generateAccountSwitch) this.generateAccountSwitch.setOnChangeValue(this, function(this:AccountingJournalTemplateEntryCell, control:MUISwitchButton, value:boolean){
            if (this._item != null) this._item.generateAccountFromBase = value;
        });

        this.valueFormulaTextField = MUIOutlet(this, "value-formula-tf", "MUITextField");
        if (this.valueFormulaTextField) this.valueFormulaTextField.setOnChangeText(this, function(this:AccountingJournalTemplateEntryCell, control:MUITextField, value:string){
            if (this._item != null) this._item.valueFormula = value;
        });

        this.sideSegmented = MUIOutlet(this, "side-segmented", "MUISegmentedControl");
        if (this.sideSegmented) this.sideSegmented.setAction(this, function(this:AccountingJournalTemplateEntryCell, control, index){
            if (this._item != null) this._item.entrySide = index; // 0 = debit, 1 = credit
        });

        this.selectionStyle = UITableViewCellSelectionStyle.None;
    }

    private _item:AccountingJournalEntryTemplate|null = null;
    set item(item:AccountingJournalEntryTemplate)
    {
        this._item = item;

        // Populate key options based on parent group matchMode
        let matchMode = (item.parent instanceof AccountingJournalGroupTemplate) ? (item.parent as AccountingJournalGroupTemplate).matchMode ?? 0 : 0;
        this._keyOptions = AccountingJournalTemplateEntryCell.keyOptions(this._eventType, matchMode);
        this._activeKey = this.activeStrategyKey();
        this.populateKeyOptions();
        this.selectCurrentKey();

        if (this.accountDropdown) this.accountDropdown.title = this.accountTitle(item) ?? MIOLocalizeString("SELECT_LEDGER_ACCOUNT", "SELECT LEDGER ACCOUNT");
        if (this.generateAccountSwitch) this.generateAccountSwitch.setOn(item.generateAccountFromBase ?? false);
        if (this.valueFormulaTextField) this.valueFormulaTextField.text = item.valueFormula;
        if (this.sideSegmented) this.sideSegmented.selectSegmentedAtIndex( item.entrySide ?? 0 );
        this.updateAccountDropdownEnabled(this._activeKey);
    }

    // The title of the ledger account of the entry, null when it has none. The account is read
    // with the entry, but it can still be on its way when the row is drawn: a fixed account
    // keeps its title as the key of the entry, so that one is shown meanwhile instead of
    // the row looking as if it had no account.
    private accountTitle(item:AccountingJournalEntryTemplate):string|null {
        let account = item.ledgerAccount;
        if (account == null) return null;
        if (account.name != null) return account.name;
        if (AccountingJournalTemplateEntryCell.isAccountKey(this._activeKey) && item.keyFormula != null && item.keyFormula.length > 0) return item.keyFormula;
        return null;
    }

    private activeStrategyKey():string|null {
        if (this._item == null) return null;
        let stored = this._item.keyFormula;
        if (stored == null || stored.length == 0) return null;
        let hasCustom = false;
        for (let i = 0; i < this._keyOptions.length; i++) {
            let opt = this._keyOptions[i];
            if (opt[2] != null && opt[2] == stored) return opt[0];
            if (opt[0] == "customLedgerAccount") hasCustom = true;
        }
        // Both account strategies store the account title, the flag tells them apart
        if (this._item.generateAccountFromBase == true) return "baseCustomAccount";
        return hasCustom ? "customLedgerAccount" : null;
    }

    private populateKeyOptions() {
        if (this.keyFormatComboBox == null) return;
        this.keyFormatComboBox.removeAllItems();
        this.keyFormatComboBox.addItem(MIOLocalizeString("SELECT", "SELECT"), -1);
        for (let i = 0; i < this._keyOptions.length; i++) {
            this.keyFormatComboBox.addItem(this.keyOptionTitle(i), i);
        }
    }

    // The analytic templates list the sales and purchase variants of a key together: the side
    // tells them apart
    private keyOptionTitle(index:number) : string {
        let option = this._keyOptions[index];
        let title = MIOLocalizeString(option[1], option[1]);
        let repeated = this._keyOptions.filter(o => o[1] == option[1]).length > 1;
        if (!repeated || option[2] == null) return title;
        if (option[2].indexOf("purchaseLedgerAccount") >= 0) return title + " (" + MIOLocalizeString("PURCHASES", "Purchases") + ")";
        if (option[2].indexOf("salesLedgerAccount") >= 0) return title + " (" + MIOLocalizeString("SALES", "Sales") + ")";
        return title;
    }

    private selectCurrentKey() {
        if (this.keyFormatComboBox == null || this._item == null) return;
        let active = this.activeStrategyKey();
        if (active == null) {
            this.keyFormatComboBox.selectItem(-1);
            return;
        }
        // the option with the stored formula (keys repeat on the analytic templates), else the key
        let stored = this._item.keyFormula;
        for (let i = 0; i < this._keyOptions.length; i++) {
            if (this._keyOptions[i][2] != null && this._keyOptions[i][2] == stored) {
                this.keyFormatComboBox.selectItem(i);
                return;
            }
        }
        for (let i = 0; i < this._keyOptions.length; i++) {
            if (this._keyOptions[i][0] == active) {
                this.keyFormatComboBox.selectItem(i);
                return;
            }
        }
        this.keyFormatComboBox.selectItem(-1);
    }

    private updateAccountDropdownEnabled(key:string|null) {
        let enabled = AccountingJournalTemplateEntryCell.isAccountKey(key);
        if (this.accountDropdown) this.accountDropdown.setEnabled(enabled);
        if (this.generateAccountSwitch) this.generateAccountSwitch.setEnabled(key == "baseCustomAccount");
    }

    private showSelectLedgerAccount(searchString:string, hideSearchBar:boolean){
        AppHelper.sharedInstance().showSelectLedgerAccountViewControllerFromView(this.accountDropdown, this._item?.ledgerAccount, searchString, hideSearchBar, null, this, this.updateLedgerAccount);
    }

    private updateLedgerAccount(controller, account:LedgerAccount){
        if (this._item == null) return;
        this._item.ledgerAccount = account;
        this._item.keyFormula = account?.name ?? null;
        if (this.accountDropdown) this.accountDropdown.title = account?.name;
    }
}
